// ============================================================================
// todo-client-mod -- 待办事项详情页
//
// 路径：/todo/:id
// 展示单条待办详情，支持快捷完成/取消完成。
// ============================================================================
import * as React from 'react';
import { useRoute } from '@hvsin/mod-route';
import { useTheme } from '../styles/use-theme.js';
import { useResponsive } from '../styles/use-responsive.js';
import { useTodo } from '../hooks/use-todo.js';
import { TodoStatusBadge } from '../components/todo-status-badge.js';
import { CheckboxCheckedIcon, CheckboxIcon, CalendarIcon, TagIcon, ClockIcon } from '../components/icons.js';
import { priorityColors, radius, transition, typography } from '../styles/theme.js';
import { KEYFRAMES_CSS } from '../styles/animations.js';
import { getTodoApiClient } from '../todo-api-client.js';
const PRIORITY_LABELS = {
    low: '低优先级', medium: '中优先级', high: '高优先级', urgent: '紧急优先级',
};
/**
 * 待办详情页
 */
export function TodoDetailPage(props) {
    const { initialData } = props;
    const { theme } = useTheme();
    const { isMobile } = useResponsive();
    const { Link, useParams } = useRoute();
    const { id } = useParams();
    const { todo, loading, error, refresh } = useTodo(id, initialData);
    // 注入全局动画样式
    React.useEffect(() => {
        const styleId = 'todo-detail-page-keyframes';
        if (!document.getElementById(styleId)) {
            const style = document.createElement('style');
            style.id = styleId;
            style.textContent = KEYFRAMES_CSS;
            document.head.appendChild(style);
        }
    }, []);
    const handleCompleteToggle = async () => {
        if (!todo)
            return;
        if (todo.status === 'completed') {
            await getTodoApiClient().updateTodo(todo._id, { status: 'pending' });
        }
        else {
            await getTodoApiClient().completeTodo(todo._id);
        }
        refresh();
    };
    const formatDate = (ts) => new Date(ts).toLocaleDateString('zh-CN', { year: 'numeric', month: 'long', day: 'numeric' });
    const formatTime = (ts) => new Date(ts).toLocaleTimeString('zh-CN', { hour: '2-digit', minute: '2-digit' });
    // 加载中
    if (loading) {
        return React.createElement('div', { style: { minHeight: '100vh', backgroundColor: theme.bg.primary, fontFamily: typography.fontFamily } }, React.createElement('div', { style: { maxWidth: '760px', margin: '0 auto', padding: '80px 24px' } }, React.createElement('div', { style: { padding: '120px 0', textAlign: 'center', color: theme.text.tertiary, fontSize: typography.fontSize.md } }, React.createElement('span', { style: { animation: 'todo-pulse 1.5s ease-in-out infinite', display: 'inline-block' } }, '加载中...'))));
    }
    // 错误或未找到
    if (error || !todo) {
        return React.createElement('div', { style: { minHeight: '100vh', backgroundColor: theme.bg.primary, fontFamily: typography.fontFamily } }, React.createElement('div', { style: { maxWidth: '760px', margin: '0 auto', padding: '80px 24px' } }, React.createElement('div', {
            style: {
                padding: '24px', backgroundColor: theme.status.dangerBg,
                border: `1px solid ${theme.status.danger}40`, borderRadius: radius.md,
                color: theme.status.danger, fontSize: typography.fontSize.md,
                textAlign: 'center', marginBottom: '24px',
            },
        }, error || '待办事项不存在'), React.createElement(Link, {
            to: '/todo',
            style: {
                display: 'inline-flex', alignItems: 'center', fontSize: typography.fontSize.base,
                color: theme.status.primary, textDecoration: 'none', fontWeight: typography.fontWeight.medium,
            },
        }, '← 返回待办列表')));
    }
    const isCompleted = todo.status === 'completed';
    const isOverdue = todo.dueDate && !isCompleted && todo.dueDate < Date.now();
    return React.createElement('div', {
        style: { minHeight: '100vh', backgroundColor: theme.bg.primary, fontFamily: typography.fontFamily, color: theme.text.primary },
    }, 
    // 顶部导航
    React.createElement('header', {
        style: {
            backgroundColor: theme.bg.secondary, borderBottom: `1px solid ${theme.border.divider}`,
            padding: isMobile ? '12px 16px' : '16px 24px',
        },
    }, React.createElement(Link, {
        to: '/todo',
        style: {
            display: 'inline-flex', alignItems: 'center', fontSize: typography.fontSize.base,
            color: theme.text.secondary, textDecoration: 'none', fontWeight: typography.fontWeight.medium,
            transition: `color ${transition.fast}`,
        },
    }, '← 返回待办列表')), 
    // 主内容
    React.createElement('main', {
        style: {
            maxWidth: '760px', margin: '0 auto',
            padding: isMobile ? '20px 16px 40px' : '32px 24px 60px',
        },
    }, React.createElement('article', {
        style: {
            backgroundColor: theme.bg.secondary, borderRadius: radius.xl,
            padding: isMobile ? '20px' : '32px',
            border: `1px solid ${theme.border.default}`,
            boxShadow: theme.shadow.sm,
            animation: 'todo-fade-in-up 0.3s ease forwards',
        },
    }, 
    // 头部
    React.createElement('header', { style: { marginBottom: '24px' } }, React.createElement('div', { style: { display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' } }, React.createElement('button', {
        onClick: handleCompleteToggle,
        style: {
            flexShrink: 0, padding: 0, border: 'none', backgroundColor: 'transparent',
            cursor: 'pointer', color: isCompleted ? theme.status.success : theme.text.tertiary,
            lineHeight: 1, transition: 'color 0.3s, transform 0.2s',
            transform: isCompleted ? 'scale(1.05)' : 'scale(1)',
        },
        title: isCompleted ? '取消完成' : '标记为完成',
    }, isCompleted ? React.createElement(CheckboxCheckedIcon) : React.createElement(CheckboxIcon)), React.createElement('h1', {
        style: {
            margin: 0, fontSize: isMobile ? typography.fontSize.xl : typography.fontSize['2xl'],
            fontWeight: typography.fontWeight.bold, lineHeight: typography.lineHeight.tight,
            color: isCompleted ? theme.text.tertiary : theme.text.primary,
            textDecoration: isCompleted ? 'line-through' : 'none', wordBreak: 'break-word',
            transition: 'color 0.3s, text-decoration 0.3s',
        },
    }, todo.title)), React.createElement('div', { style: { display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' } }, React.createElement(TodoStatusBadge, { status: todo.status }), React.createElement('span', {
        style: {
            display: 'inline-flex', alignItems: 'center', fontSize: typography.fontSize.sm,
            fontWeight: typography.fontWeight.medium, color: priorityColors[todo.priority],
            padding: '3px 10px', borderRadius: radius.sm,
            backgroundColor: `${priorityColors[todo.priority]}15`,
        },
    }, React.createElement('span', { style: { width: '8px', height: '8px', borderRadius: '50%', backgroundColor: priorityColors[todo.priority], marginRight: '6px' } }), PRIORITY_LABELS[todo.priority]))), React.createElement('hr', { style: { border: 'none', borderTop: `1px solid ${theme.border.divider}`, margin: '0 0 24px 0' } }), 
    // 详情
    React.createElement('div', { style: { display: 'flex', flexDirection: 'column', gap: '24px' } }, 
    // 描述
    todo.description && React.createElement('div', null, React.createElement('h3', { style: { margin: '0 0 10px 0', fontSize: typography.fontSize.base, fontWeight: typography.fontWeight.semibold, color: theme.text.secondary } }, '描述'), React.createElement('div', { style: { fontSize: typography.fontSize.md, lineHeight: typography.lineHeight.relaxed, color: theme.text.secondary } }, ...todo.description.split('\n').map((p, i) => React.createElement('p', { key: i, style: { margin: '0 0 12px 0' } }, p)))), 
    // 元信息
    React.createElement('div', null, React.createElement('h3', { style: { margin: '0 0 10px 0', fontSize: typography.fontSize.base, fontWeight: typography.fontWeight.semibold, color: theme.text.secondary } }, '详细信息'), React.createElement('div', { style: { display: 'flex', flexDirection: 'column', gap: '10px' } }, todo.dueDate && React.createElement('div', {
        style: {
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: '10px 12px', backgroundColor: isOverdue ? theme.status.dangerBg : theme.bg.tertiary,
            borderRadius: radius.md,
        },
    }, React.createElement('div', { style: { fontSize: typography.fontSize.sm, color: theme.text.tertiary, display: 'flex', alignItems: 'center' } }, React.createElement(CalendarIcon), React.createElement('span', { style: { marginLeft: '6px' } }, '截止日期')), React.createElement('div', { style: { fontSize: typography.fontSize.sm, fontWeight: typography.fontWeight.medium, color: isOverdue ? theme.status.danger : theme.text.primary } }, `${formatDate(todo.dueDate)} ${formatTime(todo.dueDate)}`, isOverdue && React.createElement('span', { style: { marginLeft: '8px', fontSize: typography.fontSize.xs, fontWeight: typography.fontWeight.semibold } }, '已过期'))), React.createElement('div', {
        style: {
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: '10px 12px', backgroundColor: theme.bg.tertiary, borderRadius: radius.md,
        },
    }, React.createElement('div', { style: { fontSize: typography.fontSize.sm, color: theme.text.tertiary, display: 'flex', alignItems: 'center' } }, React.createElement(ClockIcon), React.createElement('span', { style: { marginLeft: '6px' } }, '创建时间')), React.createElement('div', { style: { fontSize: typography.fontSize.sm, fontWeight: typography.fontWeight.medium, color: theme.text.primary } }, `${formatDate(todo._createdAt)} ${formatTime(todo._createdAt)}`)), todo._updatedAt !== todo._createdAt && React.createElement('div', {
        style: {
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: '10px 12px', backgroundColor: theme.bg.tertiary, borderRadius: radius.md,
        },
    }, React.createElement('div', { style: { fontSize: typography.fontSize.sm, color: theme.text.tertiary, display: 'flex', alignItems: 'center' } }, React.createElement(ClockIcon), React.createElement('span', { style: { marginLeft: '6px' } }, '更新时间')), React.createElement('div', { style: { fontSize: typography.fontSize.sm, fontWeight: typography.fontWeight.medium, color: theme.text.primary } }, `${formatDate(todo._updatedAt)} ${formatTime(todo._updatedAt)}`)))), 
    // 标签
    todo.tags && todo.tags.length > 0 && React.createElement('div', null, React.createElement('h3', { style: { margin: '0 0 10px 0', fontSize: typography.fontSize.base, fontWeight: typography.fontWeight.semibold, color: theme.text.secondary, display: 'flex', alignItems: 'center', gap: '6px' } }, React.createElement(TagIcon), '标签'), React.createElement('div', { style: { display: 'flex', flexWrap: 'wrap', gap: '6px' } }, ...todo.tags.map(tag => React.createElement('span', { key: tag, style: {
            padding: '4px 10px', backgroundColor: theme.interaction.selected, color: theme.status.primary,
            borderRadius: radius.sm, fontSize: typography.fontSize.sm, fontWeight: typography.fontWeight.medium,
        } }, tag))))))));
}
//# sourceMappingURL=todo-detail-page.js.map